import React, { useEffect, useState } from "react";
import { Text, Flex } from "@chakra-ui/react";
import { useAnimate, useInView } from "framer-motion";
import * as Sentry from "@sentry/nextjs";
import { CursorIcon } from "ui/icons";
import Balancer from "react-wrap-balancer";
import { Tab } from "./tab";
import { VennDiagramIcon } from "./venn-diagram";
import { Tooltip } from "./tooltip";
import { DiagramKey } from "./diagram-key";

function useInsightsAnimation(isActive: boolean, delay = 1) {
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const triggerAnimation = async () => {
      try {
        await animate(
          isActive
            ? [
                // Animation sequence
                ["#insights-cursor", { opacity: 1 }, { delay, duration: 0.25 }],
                [
                  "#insights-left-tooltip",
                  { scale: 1, opacity: 1 },
                  { at: "+0.1", type: "spring", stiffness: 700, damping: 30 },
                ],
                ["#insights-cursor", { x: [0, 140] }, { at: "+1", duration: 0.45 }],
                [
                  "#insights-left-tooltip",
                  { opacity: 0, scale: 0.9 },
                  { at: "-0.3", type: "spring", stiffness: 700, damping: 30 },
                ],
                ["#insights-right-tooltip", { scale: 1, opacity: 1 }, { type: "spring", stiffness: 700, damping: 30 }],
                ["#insights-cursor", { opacity: 0 }, { at: "+0.7", duration: 0.25 }],
              ]
            : [
                // Reset
                ["#insights-cursor", { x: 0, opacity: 0 }, { duration: 0.05 }],
                ["#insights-right-tooltip", { scale: 0.9, opacity: 0 }, { duration: 0.05 }],
                ["#insights-left-tooltip", { scale: 0.9, opacity: 0 }, { duration: 0.05 }],
              ],
        );
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    // Do not need `await` because there is no action after the promise
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    triggerAnimation();
  }, [isActive, delay, animate]);

  return scope;
}

export type InsightsAnimationProps = {
  delay?: number;
  title?: string;
  firstCategoryLabel?: string;
  secondCategoryLabel?: string;
};

export function InsightsAnimation({
  delay = 1,
  title = "High LTV email abandoners",
  firstCategoryLabel = "Cart abandoners",
  secondCategoryLabel = "Newsletter subscribers",
}: InsightsAnimationProps) {
  const [animationActive, setAnimationActive] = useState(false);
  const scope = useInsightsAnimation(animationActive, delay);
  const isInView = useInView(scope, { amount: 0.5 });

  useEffect(() => {
    setAnimationActive(isInView);
  }, [isInView]);

  return (
    <Flex
      ref={scope}
      align="center"
      bgGradient="linear(30deg, new.blue.150 60%, white 180%)"
      color="brand.text.light"
      boxSize="100%"
      pos="relative"
      pt={{ base: "small", xl: "bigger" }}
      px={{ base: "bigger", xl: "biggest" }}
    >
      <Flex
        align="start"
        bg="white"
        borderTopRadius="smaller"
        boxShadow="lg"
        direction="column"
        flex={1}
        h="100%"
        maxH="550px"
        mt="auto"
        maxW="440px"
        mx="auto"
        justify="start"
        pos="relative"
        p={{ base: "smaller", lg: "small" }}
      >
        <Flex borderBottom="2px" borderColor="brand.border.extraLight" gap="small" w="100%" px="tiny">
          <Tab color="brand.textSecondary.light">Members</Tab>
          <Tab isActive color="brand.text.light">
            Overlap
          </Tab>
        </Flex>

        <Flex
          direction="column"
          justify="space-between"
          gap={{ base: "smaller", xl: "small" }}
          mt={{ base: "smallest", sm: "small", xl: "big" }}
          w="100%"
          h="100%"
        >
          <Text textStyle="headline3" fontSize={{ base: "brand.sm", lg: "brand.md" }}>
            <Balancer>{title}</Balancer>
          </Text>

          <Flex
            align="center"
            justify="center"
            pos="relative"
            w={{ base: "66%", md: "62%", lg: "50%", xl: "60%" }}
            mx="auto"
          >
            <VennDiagramIcon />

            <Tooltip
              id="insights-left-tooltip"
              color="new.violet.300"
              pos="absolute"
              left={{ base: -16, lg: -24, xl: -28 }}
              top="35%"
              opacity={0}
            >
              2,988 members
            </Tooltip>

            <Tooltip
              id="insights-right-tooltip"
              color="new.green.300"
              pos="absolute"
              right={{ base: -16, lg: -32, xl: -28 }}
              top="35%"
              opacity={0}
            >
              1,974 members
            </Tooltip>

            <CursorIcon id="insights-cursor" pos="absolute" left={10} bottom="22%" opacity={0} />
          </Flex>

          <Flex direction="column" gap={{ base: "tinier", sm: "tiny" }} mb={6}>
            <DiagramKey color="new.violet.300" label={firstCategoryLabel} value="2,988" />
            <DiagramKey color="new.green.300" label={secondCategoryLabel} value="1,974" />
            <DiagramKey color="new.blue.500" label="Member overlap" value="378 or 12.65%" />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
